import { useCallback } from "react";
import { get as commsGet } from "../../../service/comms/commsApi";
import { getClientTickets } from "../../../service/comms/supportService";
import { useCachedList } from "../../../hooks/useCachedList";
import { supportTicketCache } from "../../../utils/listCache/caches";
import { toList } from "./supportUtils";

/** How many past tickets the context rail shows. */
const HISTORY_LIMIT = 10;

/**
 * The requester's own ticket history for the context rail — every past ticket
 * with its CSAT, not just the ones embedded in the current ticket's payload.
 *
 * Cached per client, so stepping through several tickets from the same person
 * (which is exactly what a repeat complaint looks like) reads it once. Degrades
 * to an empty list: the rail is context, and a failure here must not take the
 * ticket down with it.
 *
 * @param {string|null} clientId
 * @returns {{ tickets: Array<Object>, loading: boolean }}
 */
export function useClientTicketHistory(clientId) {
  const fetcher = useCallback(
    () =>
      getClientTickets(commsGet, clientId, { limit: HISTORY_LIMIT }).then((payload) =>
        toList(payload)
      ),
    [clientId]
  );

  const { data, loading } = useCachedList({
    cache: supportTicketCache,
    key: clientId ? `client:${clientId}` : null,
    fetcher,
    enabled: Boolean(clientId),
  });

  return { tickets: data || [], loading };
}
